import { useState } from "react";
import {
    Drawer,
    Box,
    Tooltip,
    IconButton,
    CircularProgress,
    Typography,
    Grid,
    Card,
    CardActionArea,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import SecurityIcon from "@mui/icons-material/Security";
import AssessmentIcon from "@mui/icons-material/Assessment";
import axiosInstance from "../../axios/axios";
import { END_POINTS } from "../../shared";
import chatIcon from "../../assets/cyber_resilence_dark.svg";
import chatSend from "../../assets/chat_send.svg";
import { toast } from "react-toastify";

interface RuleManagementChatDrawerProps {
    open: boolean;
    onClose: () => void;
}

interface ChatResponse {
    status: number;
    message: string;
    data: {
        result: string;
    };
}

interface ChatTableRow {
    date: string;
    eventCount: string;
}

const initialChatResponse: ChatResponse = {
    status: 200,
    message: "Welcome",
    data: {
        result: "",
    },
};

const quickActions = [
    "How many AWS accounts are available?",
    "How many AWS accounts are available?",
    "How many AWS accounts are available?",
];

const parseTableData = (result: string): ChatTableRow[] => {
    try {
        if (!result) return [];

        const jsonMatch = result.match(/Day-wise breakdown in json:\s*(\{[\s\S]*?\})/);
        if (jsonMatch) {
            try {
                const jsonData = JSON.parse(jsonMatch[1]);
                if (Object.keys(jsonData).length === 0) {
                    return [];
                }
                return Object.entries(jsonData).map(([date, count]) => ({
                    date,
                    eventCount: String(count),
                }));
            } catch (jsonError) {
                // Fall back to pipe parsing below
            }
        }

        return result
            .split("\n")
            .filter((line) => {
                if (!line) return false;
                const lower = line.toLowerCase();
                if (lower.includes("total count")) return false;
                if (lower.includes("day-wise breakdown")) return false;
                if (line.includes("---")) return false;
                if (lower.includes("date") && lower.includes("events count")) return false;
                return line.includes("|");
            })
            .map((line) => {
                const parts = line
                    .split("|")
                    .filter(Boolean)
                    .map((part) => part.trim());
                if (parts.length >= 2) {
                    return {
                        date: parts[0],
                        eventCount: parts[1].split(" ")[0],
                    };
                }
                return null;
            })
            .filter((row): row is ChatTableRow => row !== null);
    } catch {
        return [];
    }
};

const ChatResponseTable = ({
    data,
    isDarkMode,
}: {
    data: string;
    isDarkMode: boolean;
}) => {
    const rows = parseTableData(data);

    if (rows.length === 0) {
        return null;
    }

    return (
        <TableBody>
            {rows.map((row, index) => {
                let formattedDate = row.date;
                const dateObj = new Date(row.date);
                if (!isNaN(dateObj.getTime())) {
                    formattedDate = dateObj.toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                    });
                }

                return (
                    <TableRow
                        key={`${row.date}-${index}`}
                        sx={{
                            height: "52px",
                            bgcolor:
                                index % 2 === 0
                                    ? "transparent"
                                    : isDarkMode
                                    ? "rgba(147, 112, 219, 0.03)"
                                    : "rgba(147, 112, 219, 0.02)",
                            "&:hover": {
                                bgcolor: isDarkMode
                                    ? "rgba(147, 112, 219, 0.08)"
                                    : "rgba(147, 112, 219, 0.05)",
                            },
                            transition: "background-color 0.2s",
                        }}
                    >
                        <TableCell
                            sx={{
                                borderBottom: "1px solid",
                                borderColor: isDarkMode
                                    ? "rgba(147, 112, 219, 0.1)"
                                    : "rgba(147, 112, 219, 0.1)",
                                fontWeight: 500,
                                color: isDarkMode ? "rgba(255, 255, 255, 0.85)" : "text.primary",
                            }}
                        >
                            {formattedDate}
                        </TableCell>
                        <TableCell
                            align="center"
                            sx={{
                                borderBottom: "1px solid",
                                borderColor: isDarkMode
                                    ? "rgba(147, 112, 219, 0.1)"
                                    : "rgba(147, 112, 219, 0.1)",
                                fontWeight: 600,
                                color: isDarkMode ? "rgba(147, 112, 219, 0.9)" : "rgba(98, 0, 234, 0.8)",
                            }}
                        >
                            {row.eventCount}
                        </TableCell>
                    </TableRow>
                );
            })}
        </TableBody>
    );
};

const extractSummary = (result: string): string | null => {
    if (!result) return null;
    const summaryMatch1 = result.split("\nSummary:\n")[1];
    if (summaryMatch1) return summaryMatch1.trim();

    const summaryMatch2 = result.split("\nSummary: ")[1];
    if (summaryMatch2) return summaryMatch2.trim();

    return null;
};

const hasSummary = (result: string): boolean => {
    if (!result) return false;
    return result.includes("\nSummary:\n") || result.includes("\nSummary: ");
};

const extractTotalCount = (result: string): string | null => {
    if (!result) return null;
    const totalCountMatch = result
        .split("\n")
        .find((line) =>
            ["total count", "total events", "total scans", "total accepted risk"].some((text) =>
                line.toLowerCase().includes(text)
            )
        );
    return totalCountMatch?.trim() || null;
};

const hasTableData = (result: string): boolean => {
    if (!result) return false;
    if (result.includes("Day-wise breakdown in json:")) {
        const jsonMatch = result.match(/Day-wise breakdown in json:\s*(\{[\s\S]*?\})/);
        if (jsonMatch) {
            try {
                const jsonData = JSON.parse(jsonMatch[1]);
                return Object.keys(jsonData).length > 0;
            } catch {
                return false;
            }
        }
        return false;
    }

    return result.split("\n").some((line) => line.includes("|"));
};

const hasNoData = (result: string): boolean => {
    if (!result) return false;
    if (result.includes("Day-wise breakdown in json: {}")) return true;

    const lower = result.toLowerCase();
    return (
        lower.includes("no matching events found") ||
        lower.includes("no data found") ||
        lower.includes("no events found")
    );
};

const RuleManagementChatDrawer = ({ open, onClose }: RuleManagementChatDrawerProps) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const [chatResponse, setChatResponse] = useState<ChatResponse>(initialChatResponse);
    const [inputValue, setInputValue] = useState("");
    const [isLoadingChat, setIsLoadingChat] = useState(false);

    const resetChatState = () => {
        setInputValue("");
        setChatResponse(initialChatResponse);
    };

    const handleCardClick = async (text: string) => {
        setInputValue(text);

        if (!text.trim()) return;

        setIsLoadingChat(true);
        try {
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await axiosInstance.get(END_POINTS.SECURITY_SCAN_CHAT, {
                params: {
                    query: text.trim(),
                    timezone: userTimezone,
                },
                paramsSerializer: (params) =>
                    Object.entries(params)
                        .map(([key, value]) => `${key}=${value}`)
                        .join("&"),
            });

            if (response.data) {
                setChatResponse(initialChatResponse);
                setTimeout(() => {
                    setChatResponse(response.data as ChatResponse);
                }, 0);
            }
        } catch (error) {
            toast.error("Error fetching chat response");
            setChatResponse({
                status: 500,
                message: "Error",
                data: {
                    result: "Sorry, there was an error processing your request.",
                },
            });
        } finally {
            setIsLoadingChat(false);
        }
    };

    const handleInputSubmit = () => {
        if (inputValue.trim()) {
            handleCardClick(inputValue);
        }
    };

    const handleBackToWelcome = () => {
        setInputValue("");
        setChatResponse(initialChatResponse);
    };

    const handleClose = () => {
        resetChatState();
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            sx={{
                "& .MuiDrawer-paper": {
                    width: { xs: "100%", sm: "600px", md: "700px" },
                    maxWidth: "750px",
                },
            }}
        >
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                    sx={{
                        p: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {chatResponse.data?.result && (
                            <Tooltip title="Back to welcome" placement="bottom">
                                <IconButton
                                    onClick={handleBackToWelcome}
                                    size="small"
                                    sx={{
                                        color: theme.palette.text.secondary,
                                        "&:hover": {
                                            color: theme.palette.text.primary,
                                            bgcolor: isDarkMode
                                                ? "rgba(255, 255, 255, 0.08)"
                                                : "rgba(0, 0, 0, 0.04)",
                                        },
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>Rule Assistant</span>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        p: 2,
                        flexGrow: 1,
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 2,
                    }}
                >
                    {isLoadingChat ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <CircularProgress size={40} />
                            <Typography variant="body1" color="text.secondary">
                                Processing your request...
                            </Typography>
                        </Box>
                    ) : !chatResponse.data?.result ? (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                maxWidth: "800px",
                                margin: "0 auto",
                                width: "100%",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    textAlign: "center",
                                    mb: 4,
                                }}
                            >
                                <Box
                                    sx={{
                                        mb: 2,
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <img
                                        src={chatIcon}
                                        alt="Chat"
                                        style={{
                                            width: "48px",
                                            height: "48px",
                                        }}
                                    />
                                </Box>
                                <Typography variant="body1" sx={{ fontSize: "1.1rem" }}>
                                    Hi, I am your rule assistant. How can I help you today?
                                </Typography>
                            </Box>

                            <Box sx={{ width: "100%" }}>
                                <Grid container spacing={2}>
                                    {quickActions.map((label) => (
                                        <Grid item xs={12} sm={4} key={label}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    height: "100%",
                                                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                        transform: "translateY(-2px)",
                                                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                                                    },
                                                }}
                                            >
                                                <CardActionArea
                                                    onClick={() => handleCardClick(label)}
                                                    sx={{
                                                        p: 2,
                                                        height: "100%",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        textAlign: "center",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: "text.secondary",
                                                        }}
                                                    >
                                                        {label}
                                                    </Typography>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                width: "100%",
                            }}
                        >
                            {extractTotalCount(chatResponse.data.result) &&
                                !hasNoData(chatResponse.data.result) && (
                                    <Box
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? "rgba(255, 255, 255, 0.05)"
                                                : "rgba(0, 0, 0, 0.02)",
                                            p: 2,
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1,
                                        }}
                                    >
                                        <SecurityIcon sx={{ color: theme.palette.primary.main, mt: 0.5 }} />
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 500,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {extractTotalCount(chatResponse.data.result)}
                                        </Typography>
                                    </Box>
                                )}

                            {hasNoData(chatResponse.data.result) && (
                                <Box
                                    sx={{
                                        bgcolor: isDarkMode
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                        p: 3,
                                        borderRadius: 2,
                                        border: `1px solid ${
                                            isDarkMode ? "rgba(147, 112, 219, 0.1)" : "rgba(147, 112, 219, 0.15)"
                                        }`,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                        gap: 2,
                                        maxWidth: "600px",
                                        mx: "auto",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? "rgba(147, 112, 219, 0.1)"
                                                : "rgba(147, 112, 219, 0.05)",
                                            p: 2,
                                            borderRadius: "50%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <AssessmentIcon
                                            sx={{
                                                color: isDarkMode
                                                    ? "rgba(147, 112, 219, 0.9)"
                                                    : "rgba(147, 112, 219, 1)",
                                                fontSize: "32px",
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="h6"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: 600,
                                                mb: 1,
                                            }}
                                        >
                                            No Data Found
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.secondary,
                                                lineHeight: 1.6,
                                            }}
                                        >
                                            No matching events were found for your query. Try adjusting your search
                                            criteria or time range.
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {hasTableData(chatResponse.data.result) && !hasNoData(chatResponse.data.result) && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        width: "60%",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "#F8F9FA",
                                        border: `1px solid ${
                                            isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
                                        }`,
                                    }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: "0.95rem",
                                                        bgcolor: isDarkMode
                                                            ? "rgba(147, 112, 219, 0.1)"
                                                            : "rgba(147, 112, 219, 0.05)",
                                                        borderBottom: `1px solid ${
                                                            isDarkMode
                                                                ? "rgba(147, 112, 219, 0.2)"
                                                                : "rgba(147, 112, 219, 0.1)"
                                                        }`,
                                                        color: isDarkMode
                                                            ? "rgba(147, 112, 219, 0.9)"
                                                            : "rgba(147, 112, 219, 1)",
                                                        py: 2.5,
                                                        height: "56px",
                                                    }}
                                                >
                                                    Last Updated
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: "0.95rem",
                                                        bgcolor: isDarkMode
                                                            ? "rgba(147, 112, 219, 0.1)"
                                                            : "rgba(147, 112, 219, 0.05)",
                                                        borderBottom: `1px solid ${
                                                            isDarkMode
                                                                ? "rgba(147, 112, 219, 0.2)"
                                                                : "rgba(147, 112, 219, 0.1)"
                                                        }`,
                                                        color: isDarkMode
                                                            ? "rgba(147, 112, 219, 0.9)"
                                                            : "rgba(147, 112, 219, 1)",
                                                        py: 2.5,
                                                        height: "56px",
                                                    }}
                                                >
                                                    Accepted Risk
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <ChatResponseTable data={chatResponse.data.result} isDarkMode={isDarkMode} />
                                    </Table>
                                </Paper>
                            )}

                            {chatResponse?.data?.result &&
                                hasSummary(chatResponse.data.result) &&
                                !hasNoData(chatResponse.data.result) && (
                                    <Box
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? "rgba(255, 255, 255, 0.05)"
                                                : "rgba(0, 0, 0, 0.02)",
                                            p: 2,
                                            borderRadius: 1,
                                            mt: hasTableData(chatResponse.data.result) ? 2 : 0,
                                            border: `1px solid ${
                                                isDarkMode ? "rgba(147, 112, 219, 0.1)" : "rgba(147, 112, 219, 0.15)"
                                            }`,
                                            display: "flex",
                                            gap: 2,
                                            alignItems: "flex-start",
                                            width: hasTableData(chatResponse.data.result) ? "60%" : "auto",
                                            maxWidth: hasTableData(chatResponse.data.result) ? "60%" : "800px",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                bgcolor: isDarkMode
                                                    ? "rgba(147, 112, 219, 0.1)"
                                                    : "rgba(147, 112, 219, 0.05)",
                                                p: 1,
                                                borderRadius: 1,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                        >
                                            <SecurityIcon
                                                sx={{
                                                    color: isDarkMode ? "rgba(147, 112, 219, 0.9)" : "rgba(147, 112, 219, 1)",
                                                    fontSize: "20px",
                                                }}
                                            />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: isDarkMode ? "rgba(147, 112, 219, 0.9)" : "rgba(147, 112, 219, 1)",
                                                    mb: 0.5,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Summary
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.primary,
                                                    lineHeight: 1.6,
                                                }}
                                            >
                                                {extractSummary(chatResponse.data.result)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                            {!hasTableData(chatResponse.data.result) && !hasNoData(chatResponse.data.result) && (
                                <Box
                                    sx={{
                                        bgcolor: isDarkMode
                                            ? "rgba(255, 255, 255, 0.05)"
                                            : "rgba(0, 0, 0, 0.02)",
                                        p: 3,
                                        borderRadius: 2,
                                        border: `1px solid ${
                                            isDarkMode ? "rgba(147, 112, 219, 0.1)" : "rgba(147, 112, 219, 0.15)"
                                        }`,
                                        display: "flex",
                                        gap: 2,
                                        alignItems: "flex-start",
                                        maxWidth: "800px",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            bgcolor: isDarkMode
                                                ? "rgba(147, 112, 219, 0.1)"
                                                : "rgba(147, 112, 219, 0.05)",
                                            p: 1.5,
                                            borderRadius: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            flexShrink: 0,
                                        }}
                                    >
                                        <AssessmentIcon
                                            sx={{
                                                color: isDarkMode ? "rgba(147, 112, 219, 0.9)" : "rgba(147, 112, 219, 1)",
                                                fontSize: "24px",
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                lineHeight: 1.7,
                                                fontSize: "0.95rem",
                                                whiteSpace: "pre-line",
                                            }}
                                        >
                                            {chatResponse.data.result}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        p: 2,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: isDarkMode ? "background.paper" : "grey.50",
                        mt: "auto",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            bgcolor: isDarkMode ? "rgba(147, 112, 219, 0.03)" : "#FFFFFF",
                            borderRadius: 2,
                            border: `1px solid ${
                                isDarkMode ? "rgba(147, 112, 219, 0.1)" : "rgba(147, 112, 219, 0.15)"
                            }`,
                            py: 0.5,
                            px: 1.5,
                            "&:hover": {
                                bgcolor: isDarkMode ? "rgba(147, 112, 219, 0.05)" : "rgba(147, 112, 219, 0.02)",
                                border: `1px solid ${
                                    isDarkMode ? "rgba(147, 112, 219, 0.2)" : "rgba(147, 112, 219, 0.3)"
                                }`,
                            },
                            transition: "all 0.2s ease",
                            minHeight: "40px",
                            boxShadow: isDarkMode ? "none" : "0 2px 4px rgba(147, 112, 219, 0.05)",
                        }}
                    >
                        <TextField
                            fullWidth
                            placeholder="Type your question here..."
                            variant="standard"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleInputSubmit();
                                }
                            }}
                            InputProps={{
                                disableUnderline: true,
                            }}
                            sx={{
                                "& .MuiInputBase-root": {
                                    color: isDarkMode ? "rgba(147, 112, 219, 0.9)" : "rgba(147, 112, 219, 1)",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                },
                                "& .MuiInputBase-input": {
                                    padding: "0 4px",
                                    height: "32px",
                                    lineHeight: "32px",
                                    fontSize: "14px",
                                    "&::placeholder": {
                                        color: isDarkMode ? "rgba(147, 112, 219, 0.5)" : "rgba(147, 112, 219, 0.4)",
                                        opacity: 1,
                                        fontWeight: 400,
                                    },
                                },
                            }}
                        />
                        <IconButton
                            onClick={handleInputSubmit}
                            disabled={isLoadingChat}
                            size="medium"
                            sx={{
                                color: isDarkMode ? "rgba(147, 112, 219, 0.9)" : "rgba(147, 112, 219, 0.8)",
                                "&:hover": {
                                    bgcolor: isDarkMode ? "rgba(147, 112, 219, 0.1)" : "rgba(147, 112, 219, 0.05)",
                                    color: isDarkMode ? "rgba(147, 112, 219, 1)" : "rgba(147, 112, 219, 1)",
                                },
                                padding: "4px",
                                transition: "all 0.2s ease",
                            }}
                        >
                            {isLoadingChat ? (
                                <CircularProgress size={20} sx={{ color: "inherit" }} />
                            ) : (
                                <img
                                    src={chatSend}
                                    alt="Send"
                                    style={{
                                        width: "24px",
                                        height: "24px",
                                        opacity: isLoadingChat ? 0.7 : 1,
                                        transition: "opacity 0.2s ease",
                                    }}
                                />
                            )}
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Drawer>
    );
};

export default RuleManagementChatDrawer;

